import React, { useState, useEffect, useCallback } from "react";
import { Banner, BlockStack, ProgressBar } from "@shopify/polaris";
import { getReadBanner, createReadBanner, useBanners } from "../../../api_utils/requests";

const secondsInHumanReadable = (seconds: number): string => {
  if (seconds > 60 * 60) {
    const hours = Math.ceil(seconds / (60 * 60));
    return `${hours}h`;
  } else if (seconds > 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}m`;
  } else {
    return `${seconds}s`;
  }
};

function ImportProgressBanner(): React.ReactElement {
  const [alreadySeen, setAlreadySeen] = useState<boolean>(true);
  const { data: banner } = useBanners();

  const fetchBanner = async (): Promise<void> => {
    await getReadBanner("store_importing").catch((e: unknown) => {
      if (e instanceof Error && e.message.includes("404")) {
        setAlreadySeen(false);
      }
    });
  };

  useEffect(() => {
    void fetchBanner();
  }, []);

  const dismissBanner = useCallback(async (): Promise<void> => {
    setAlreadySeen(true);
    await createReadBanner("store_importing");
  }, [setAlreadySeen]);

  return !alreadySeen && banner?.info ? (
    <>
      <Banner onDismiss={dismissBanner} title={banner.info} tone="info">
        <BlockStack gap="200">
          <p>
            <p>Full functionality will be ready when it&apos;s done.</p>
            <p>This can take some time if your store has a lot of products or sales.</p>
          </p>
          <p>{banner.progress ? <ProgressBar progress={banner.progress} /> : null}</p>
          {banner.estimate_time_remaining ? (
            <p>Estimated time remaining: {secondsInHumanReadable(banner.estimate_time_remaining)}</p>
          ) : null}
        </BlockStack>
      </Banner>
      <br />
    </>
  ) : null;
};

const MemoizedImportProgressBanner = React.memo(ImportProgressBanner);

export default MemoizedImportProgressBanner;
